import React, { useEffect, useState } from 'react'
import { Box, IconButton, Typography, Chip, CircularProgress, Divider } from '@mui/material'
import { Button, Dialog, DialogContent, makeStyles, TextField } from '@material-ui/core'
import { Close, Download, Print, CropPortrait, CropLandscape } from '@material-ui/icons'
import { useSelector } from 'react-redux'
import { first } from 'lodash'
import {
  applicableBadgeConfiguration,
  updateRecentBadgeLabels,
  clearRecentBadgeLabels
} from 'store/badges'
import BadgePreview from './Configurations/BadgePreview'

const PAGE_SIZE_OPTIONS = [
  { id: 'letter', label: 'US Letter' },
  { id: 'a4', label: 'A4' }
]

const PrintBadgesModal = ({ open, onClose, subjects, onPrint, onDownload, loading }) => {
  const classes = useStyles()
  const { conference } = useSelector((state) => state.conference)

  const subjectType = first(subjects)?.type || 'Attendee'

  const [pageSize, setPageSize] = useState(() => localStorage.getItem('badgePageSize') || 'letter')
  const [orientation, setOrientation] = useState(
    () => localStorage.getItem('badgeOrientation') || 'landscape'
  )
  const [bannerLabel, setBannerLabel] = useState('')
  const [recentLabelsByType, setRecentLabelsByType] = useState({})
  const [badgeConfig, setBadgeConfig] = useState(null)
  const [loadingConfig, setLoadingConfig] = useState(false)
  const [clearingLabels, setClearingLabels] = useState(false)

  const recentLabels = recentLabelsByType[subjectType] || []

  useEffect(() => {
    if (!open) return
    setBadgeConfig(null)
    setRecentLabelsByType({})
    setBannerLabel('')

    if (conference?.id && subjectType) {
      setLoadingConfig(true)
      applicableBadgeConfiguration(subjectType, conference.id)
        .then((config) => {
          setBadgeConfig(config)
          const labelsByType =
            config?.recentLabels && !Array.isArray(config.recentLabels) ? config.recentLabels : {}
          setRecentLabelsByType(labelsByType)
          const recents = labelsByType[subjectType] || []
          setBannerLabel(recents[0] || subjectType)
          const savedOrientation = localStorage.getItem('badgeOrientation')
          if (!savedOrientation && config?.badgeOrientation) {
            setOrientation(config.badgeOrientation)
          }
        })
        .finally(() => setLoadingConfig(false))
    }
  }, [open, subjectType, conference?.id])

  const handlePageSizeChange = (_, val) => {
    if (!val) return
    setPageSize(val)
    localStorage.setItem('badgePageSize', val)
  }

  const handleOrientationChange = (_, val) => {
    if (!val) return
    setOrientation(val)
    localStorage.setItem('badgeOrientation', val)
  }

  const handleClearRecents = async () => {
    setClearingLabels(true)
    const updated = await clearRecentBadgeLabels(badgeConfig?.id, subjectType, recentLabelsByType)
    setRecentLabelsByType(updated)
    setClearingLabels(false)
  }

  const handleSubmit = (type) => {
    if (type === 'print') {
      onPrint({ pageSize, orientation, bannerLabel })
    } else {
      onDownload({ pageSize, orientation, bannerLabel })
    }
    updateRecentBadgeLabels(badgeConfig?.id, subjectType, bannerLabel, recentLabelsByType).then(
      setRecentLabelsByType
    )
  }

  const configForPreview = badgeConfig
    ? { ...badgeConfig, subjectLabels: { [subjectType]: bannerLabel } }
    : null

  const badgeCount = subjects?.length || 0

  return (
    <Dialog fullWidth maxWidth="lg" open={open} onClose={!loading ? onClose : undefined}>
      <DialogContent className={classes.dialogContent}>
        {/* ── Header ── */}
        <Box className={classes.header}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Print style={{ color: '#15b4db', fontSize: 22 }} />
            <Typography variant="h6" style={{ fontWeight: 600, fontSize: 18 }}>
              Print Badges
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} disabled={loading}>
            <Close fontSize="small" />
          </IconButton>
        </Box>

        {/* ── Body: left settings | right preview ── */}
        <Box className={classes.body}>
          {/* ── Left panel ── */}
          <Box className={classes.leftPanel}>
            {/* Banner label */}
            <Box>
              <Typography variant="caption" className={classes.sectionCaption}>
                BADGE LABEL
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                value={bannerLabel}
                onChange={(e) => setBannerLabel(e.target.value)}
                placeholder="Enter banner label"
                className={classes.labelInput}
                error={bannerLabel.length > 50}
              />
              {bannerLabel.length > 50 ? (
                <Typography className={classes.helperText} style={{ color: '#f44336' }}>
                  Label must not exceed 50 characters.
                </Typography>
              ) : (
                <Typography className={classes.helperText}>
                  Customizing the banner label here only applies to this print job.
                </Typography>
              )}
            </Box>

            {/* Recent labels */}
            <Box>
              <Box className={classes.recentsHeader}>
                <Typography variant="caption" className={classes.sectionCaption}>
                  RECENT LABELS
                </Typography>
                {recentLabels.length > 0 && (
                  <Button
                    size="small"
                    onClick={handleClearRecents}
                    disabled={clearingLabels}
                    className={classes.clearBtn}>
                    {clearingLabels ? <CircularProgress size={10} /> : 'Clear'}
                  </Button>
                )}
              </Box>
              {recentLabels.length > 0 ? (
                <Box className={classes.chipRow}>
                  {recentLabels.map((label) => (
                    <Chip
                      key={label}
                      label={label}
                      size="small"
                      variant={label === bannerLabel ? 'filled' : 'outlined'}
                      onClick={() => setBannerLabel(label)}
                      className={classes.recentChip}
                      sx={
                        label === bannerLabel
                          ? { backgroundColor: '#15b4db', color: '#fff', borderColor: '#15b4db' }
                          : { borderColor: '#ccc', color: '#555' }
                      }
                    />
                  ))}
                </Box>
              ) : (
                <Typography className={classes.noRecents}>No recent labels yet.</Typography>
              )}
            </Box>

            <Divider />

            {/* Orientation + Page size */}
            <Box
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between'
              }}>
              <Box style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <Typography variant="caption" className={classes.sectionCaption}>
                  ORIENTATION
                </Typography>
                <Box className={classes.segmentedGroup}>
                  {[
                    { value: 'landscape', label: 'Landscape', Icon: CropLandscape },
                    { value: 'portrait', label: 'Portrait', Icon: CropPortrait }
                  ].map(({ value, label, Icon }) => (
                    <Box
                      key={value}
                      className={`${classes.segmentedOption} ${
                        orientation === value ? classes.segmentedOptionActive : ''
                      }`}
                      onClick={() => handleOrientationChange(null, value)}>
                      <Icon style={{ fontSize: 13, marginRight: 4 }} />
                      {label}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <Typography variant="caption" className={classes.sectionCaption}>
                  PAGE SIZE
                </Typography>
                <Box className={classes.segmentedGroup}>
                  {PAGE_SIZE_OPTIONS.map((opt) => (
                    <Box
                      key={opt.id}
                      className={`${classes.segmentedOption} ${
                        pageSize === opt.id ? classes.segmentedOptionActive : ''
                      }`}
                      onClick={() => handlePageSizeChange(null, opt.id)}>
                      {opt.label}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>

          {/* ── Right panel: preview ── */}
          <Box className={classes.rightPanel}>
            {/* Preview area */}
            <Box className={classes.previewArea}>
              {loadingConfig ? (
                <Box className={classes.previewCenter}>
                  <CircularProgress size={28} />
                  <Typography className={classes.previewHint} style={{ marginTop: 8 }}>
                    Loading configuration...
                  </Typography>
                </Box>
              ) : configForPreview ? (
                <BadgePreview
                  badgeConfig={configForPreview}
                  orientation={orientation}
                  previewAs={subjectType}
                  subject={first(subjects)}
                />
              ) : (
                <Box className={classes.previewCenter}>
                  <Typography className={classes.previewHint}>
                    No configuration found for {subjectType}.
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        {/* ── Footer actions ── */}
        <Divider style={{ margin: '20px 0 16px' }} />
        <Box className={classes.footer}>
          <Box className={classes.footerActions}>
            <Button size="large" onClick={onClose} disabled={loading} className={classes.cancelBtn}>
              Cancel
            </Button>
            <Button
              variant="outlined"
              color="primary"
              size="large"
              startIcon={loading ? <CircularProgress size={15} color="inherit" /> : <Print />}
              disabled={loading || bannerLabel.length > 50}
              onClick={() => handleSubmit('print')}
              className={classes.actionBtn}>
              Print {badgeCount} badge{badgeCount !== 1 ? 's' : ''}
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={loading ? <CircularProgress size={15} color="inherit" /> : <Download />}
              disabled={loading || bannerLabel.length > 50}
              onClick={() => handleSubmit('download')}
              className={classes.actionBtn}>
              Download PDF
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default PrintBadgesModal

const useStyles = makeStyles(() => ({
  dialogContent: {
    padding: '20px 24px !important'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '20px'
  },
  body: {
    display: 'flex',
    gap: '0px',
    alignItems: 'flex-start',
    minHeight: '360px'
  },

  /* ── Left panel ── */
  leftPanel: {
    flex: '0 0 380px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    paddingRight: '24px',
    borderRight: '1px solid #f0f0f0'
  },
  sectionCaption: {
    fontSize: '11px !important',
    fontWeight: '700 !important',
    color: '#888 !important',
    letterSpacing: '0.06em',
    marginBottom: '6px',
    lineHeight: '1.4 !important'
  },
  labelInput: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      fontSize: '14px'
    }
  },
  helperText: {
    whiteSpace: 'nowrap',
    fontSize: '11px !important',
    color: '#aaa',
    marginTop: '5px !important'
  },
  recentsHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '6px'
  },
  clearBtn: {
    fontSize: '12px',
    padding: '1px 6px',
    color: '#aaa',
    minWidth: 'unset',
    textTransform: 'none'
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px'
  },
  recentChip: {
    cursor: 'pointer',
    fontSize: '12px'
  },
  noRecents: {
    fontSize: '12px',
    color: '#ccc',
    fontStyle: 'italic'
  },
  segmentedGroup: {
    display: 'inline-flex',
    border: '1.5px solid #e0e0e0',
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: '#f5f5f5'
  },
  segmentedOption: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '11px',
    fontWeight: 500,
    color: '#888',
    cursor: 'pointer',
    padding: '5px 13px',
    backgroundColor: 'transparent',
    userSelect: 'none',
    transition: 'background-color 0.15s, color 0.15s',
    whiteSpace: 'nowrap',
    '&:not(:last-child)': {
      borderRight: '1.5px solid #e0e0e0'
    },
    '&:hover': { color: '#15b4db', backgroundColor: '#e6f7fb' }
  },
  segmentedOptionActive: {
    backgroundColor: '#fff',
    color: '#15b4db',
    fontWeight: 700,
    boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.06)'
  },

  /* ── Right panel ── */
  rightPanel: {
    flex: 1,
    paddingLeft: '24px',
    display: 'flex',
    flexDirection: 'column'
  },
  previewArea: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    minHeight: '280px',
    padding: '16px'
  },
  previewCenter: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
  },
  previewHint: {
    fontSize: '13px',
    color: '#aaa'
  },

  /* ── Footer ── */
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end'
  },
  footerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  cancelBtn: {
    textTransform: 'none',
    color: '#555'
  },
  actionBtn: {
    textTransform: 'none'
  }
}))
